import jwtDecode from "jwt-decode";
import refresh from "feature/login/api/refresh";
import { MAX_TIMEOUT_MS, isJwtError, msUntilRenewal } from "./jwt";
import useMergeReducer from "hook/useMergeReducer";
import { api, apiQuery } from "api";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Async,
  Maybe,
  and,
  chain,
  curry,
  flip,
  getPathOr,
  getProp,
  isEmpty,
  isFunction,
  isString,
  isTruthy,
  not,
  option,
  pipe,
  safe,
  tap,
  tryCatch,
} from "crocks";

/**
 * @typedef {object} AuthContextValue
 * @property {bool} isAuthorized: bool,
 * @property {(newState: object) => void} update
 * @property {(variables: object) => (query: string) => Async} api
 * @property {(query: string) => (variables: object) => Async} apiWithQuery
 * @property {(query: string) => Async} apiQuery
 */

const AuthContext = createContext();
const isValid = and(isString, not(isEmpty));

const bearer = (token) => ({ Authorization: `Bearer ${token}` });

const AuthContextProvider = ({ hasuraAdminSecret, children }) => {
  const [isAuthorized, setAuthorized] = useState(
    hasuraAdminSecret ? true : null,
  );
  const [state, setState] = useMergeReducer({
    token: null,
    refreshToken: null,
  });

  const userData = useMemo(
    () =>
      pipe(
        getProp("token"),
        chain(safe(isTruthy)),
        chain((token) =>
          tryCatch(() => jwtDecode(token))().either(Maybe.Nothing, Maybe.Just),
        ),
        chain(getProp("userData")),
        option(null),
      )(state),
    [state],
  );

  // TODO: hard-coded application id
  const roles = useMemo(
    () =>
      userData?.registrations?.find(
        (r) => r?.applicationId === "fe74ec6e-e828-487b-984c-3c6e52f541b2",
      )?.roles,
    [userData],
  );
  const isAdmin = useMemo(
    () => roles?.includes("admin") || hasuraAdminSecret,
    [roles],
  );

  const fail = useCallback(
    (err) => {
      setAuthorized(false);
      setState({ token: null, refreshToken: null });
    },
    [setAuthorized, setState],
  );

  const tokensRef = useRef(state);
  tokensRef.current = state;
  const renewalRef = useRef(null);

  // One renewal at a time: the refresh token may be single-use, so parallel
  // requests that all hit an expired JWT must share the same refresh call.
  const renewToken = useCallback(() => {
    if (renewalRef.current) return renewalRef.current;

    const refreshToken =
      tokensRef.current.refreshToken || localStorage.getItem("refreshToken");

    renewalRef.current = refresh(refreshToken)
      .toPromise()
      .then((tokens) => {
        tokensRef.current = { ...tokensRef.current, ...tokens };
        setState(tokens);
        return tokens;
      })
      .catch((error) => {
        // Offline (fetch TypeError): keep the session and retry later.
        if (!(error instanceof TypeError)) fail(error);
        throw error;
      })
      .finally(() => {
        renewalRef.current = null;
      });

    return renewalRef.current;
  }, [setState, fail]);

  /**
   * @type {(variables: Object) => (query: string) => Async}
   */
  const authorizedApi = useMemo(() => {
    if (hasuraAdminSecret)
      return api({ "x-hasura-admin-secret": hasuraAdminSecret });
    if (!isValid(state.token)) return apiQuery;

    return curry((variables, query) =>
      api(bearer(tokensRef.current.token), variables, query).bichain(
        (error) =>
          isJwtError(error)
            ? Async.fromPromise(renewToken)().chain(({ token }) =>
                api(bearer(token), variables, query),
              )
            : Async.Rejected(error),
        Async.Resolved,
      ),
    );
  }, [state.token, renewToken]);

  useEffect(() => {
    if (hasuraAdminSecret || !isValid(state.token)) return;

    const renewSilently = () => renewToken().catch(() => {});
    const delay = msUntilRenewal(state.token);
    const timer =
      delay === null
        ? null
        : setTimeout(renewSilently, Math.min(delay, MAX_TIMEOUT_MS));

    // Timers stall while a laptop sleeps or a tab is frozen; check on wake.
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      if (msUntilRenewal(tokensRef.current.token) === 0) renewSilently();
    };

    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
    };
  }, [state.token, renewToken]);

  const success = useCallback(() => setAuthorized(true), [setAuthorized]);

  const logout = useCallback(
    (callback) => {
      setAuthorized(false);
      setState({ token: null, refreshToken: null, user: null });
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      isFunction(callback) && callback();
    },
    [state, setAuthorized],
  );

  useEffect(() => {
    if (hasuraAdminSecret) return;
    getProp("refreshToken", state)
      .chain(safe(isValid))
      .map(tap((value) => localStorage.setItem("refreshToken", value)))
      .alt(safe(isValid, localStorage.getItem("refreshToken")))
      .either(fail, (refreshToken) => {
        if (state.token) return success();
        refresh(refreshToken).map(tap(setState)).fork(fail, success);
      });
  }, [state, fail, success]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        isAuthorized,
        update: setState,
        api: authorizedApi,
        apiWithQuery: flip(authorizedApi),
        apiQuery: authorizedApi(null),
        logout,
        userData,
        roles,
        isAdmin,
        company_id: getPathOr(null, ["data", "company_id"], userData),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * @type {() => AuthContextValue}
 * @throws {Error}
 */
const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined)
    throw new Error("useAuth must be used within a AuthProvider");
  return context;
};

export { AuthContextProvider, useAuth };
